import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import {
  redactPath,
  redactSensitiveText,
  redactUrl,
} from "./utils/analyticsRedaction";
import "./index.css";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { PostHogProvider } from "posthog-js/react";
import { Provider } from "mobx-react";
import posthog from "./utils/posthog";
import RootStoreInstance from "@/state/RootStore";
import { ClerkProvider } from "@clerk/react-router";
import { getRouterBasename } from "./routing/routerBasename";
import { IS_E2E } from "./lib/isE2E";
import { installTestHooks } from "./lib/e2eTestHooks";
import { esES } from "@clerk/localizations";
import { TooltipProvider } from "@/components/ui/tooltip";
import i18n, { ensureI18nInitialized, getCurrentLanguage } from "@/i18n";
import { getDocumentTitle, getOgLocale } from "@/i18n/metaCopy";
import { applyAppTheme } from "@/config/appTheme";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION as string | undefined,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Sentry is a second analytics sink for the same URLs PostHog receives.
    // Organisation access codes (`?code=`), email bearer tokens (`?t=`) and
    // referral codes (`/refer/<code>`) are credentials, so they are scrubbed
    // here too — otherwise redacting them in PostHog alone just moves the leak.
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = redactUrl(event.request.url) as string;
      }
      if (event.transaction) {
        event.transaction = redactPath(event.transaction) as string;
      }
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data?.url) {
            crumb.data.url = redactUrl(crumb.data.url);
          }
          if (crumb.message) {
            crumb.message = redactSensitiveText(crumb.message) as string;
          }
        }
      }
      if (event.message) {
        event.message = redactSensitiveText(event.message) as string;
      }
      if (event.exception?.values) {
        for (const exception of event.exception.values) {
          if (exception.value) {
            exception.value = redactSensitiveText(exception.value) as string;
          }
        }
      }
      return event;
    },
    beforeSendTransaction(event) {
      if (event.request?.url) {
        event.request.url = redactUrl(event.request.url) as string;
      }
      if (event.transaction) {
        event.transaction = redactPath(event.transaction) as string;
      }
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (crumb.data?.url) {
        crumb.data.url = redactUrl(crumb.data.url);
      }
      if (crumb.message) {
        crumb.message = redactSensitiveText(crumb.message) as string;
      }
      return crumb;
    },
  });
}

// Router basename = the subfolder this artifact is actually mounted at (see getRouterBasename).
const appType = (import.meta.env.VITE_APP_TO_BUILD as string) || "";

const basename = getRouterBasename({
  baseUrl: import.meta.env.BASE_URL,
  pathname: window.location.pathname,
  isDev: import.meta.env.DEV,
});

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

function setOgLocaleMeta(locale: string) {
  try {
    let tag = document.querySelector(
      'meta[property="og:locale"]',
    ) as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", "og:locale");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", locale);
  } catch {
    // ignore — meta updates are best-effort
  }
}

function applyLocaleToDocument(language: string) {
  try {
    document.documentElement.lang = language;
    document.documentElement.dir = "ltr";
    document.title = getDocumentTitle(i18n.t.bind(i18n), appType);
    setOgLocaleMeta(getOgLocale(language as "en" | "es"));
  } catch {
    // SSR safety; ignore.
  }
}

/**
 * Wraps ClerkProvider so its `localization` prop reactively updates when
 * the user switches language via LanguageSelector. Without this, Clerk's
 * sign-in/sign-up forms stay in whichever language was active at boot.
 */
function ReactiveClerkProvider({
  children,
  publishableKey,
}: {
  children: React.ReactNode;
  publishableKey: string;
}) {
  const [language, setLanguage] = useState(() => getCurrentLanguage());

  useEffect(() => {
    const handler = (lng: string) => setLanguage(lng as "en" | "es");
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, []);

  // Clerk historically snapshots the `localization` prop at provider mount.
  // Force a remount on language change via `key` so already-rendered Clerk
  // forms (e.g. <SignIn/>, <SignUp/>) actually flip labels. Clerk preserves
  // the active session through cookies, so the remount doesn't sign users out.
  return (
    <ClerkProvider
      key={language}
      publishableKey={publishableKey}
      localization={language === "es" ? esES : undefined}
    >
      {children}
    </ClerkProvider>
  );
}

async function bootstrap() {
  await ensureI18nInitialized();
  applyAppTheme(document.documentElement);
  applyLocaleToDocument(getCurrentLanguage());

  const root = ReactDOM.createRoot(document.getElementById("root")!, {
    onCaughtError: Sentry.reactErrorHandler(),
    onUncaughtError: Sentry.reactErrorHandler(),
  });

  root.render(
    <React.StrictMode>
      {/* Outer boundary catches failures in the provider stack itself. */}
      <AppErrorBoundary>
        <Provider store={RootStoreInstance}>
          <PostHogProvider client={posthog}>
            <BrowserRouter basename={basename}>
              <ReactiveClerkProvider publishableKey={PUBLISHABLE_KEY}>
                <TooltipProvider delayDuration={200}>
                  <App />
                </TooltipProvider>
              </ReactiveClerkProvider>
            </BrowserRouter>
          </PostHogProvider>
        </Provider>
      </AppErrorBoundary>
    </React.StrictMode>,
  );

  if (import.meta.hot) {
    import.meta.hot.dispose(() => root.unmount());
  }

  // E2E test hooks (window.__TEST__) for direct store manipulation, gated by IS_E2E
  // (import.meta.env.MODE === "e2e"). MODE is "production" in prod, so __TEST__ is
  // never attached for real users.
  if (IS_E2E) {
    (window as unknown as { __TEST__: unknown }).__TEST__ =
      installTestHooks(RootStoreInstance);
  }

  // Re-apply <html lang> / og:locale / document.title when the language
  // changes at runtime (Profile → Language picker, or another tab).
  i18n.on("languageChanged", (lng) => {
    applyLocaleToDocument(lng);
  });
}

void bootstrap();
